import { z } from 'zod';

// Validador de DNI chileno
const chileanDniValidator = (value: string): boolean => {
  if (!value) return true;
  
  const dniPattern = /^[0-9]+-[0-9kK]{1}$/;
  if (!dniPattern.test(value)) {
    return false;
  }
  
  const parts = value.split('-');
  const number = parts[0];
  const verifier = parts[1].toLowerCase();
  
  let sum = 0;
  let multiplier = 2;
  
  for (let i = number.length - 1; i >= 0; i--) {
    sum += parseInt(number.charAt(i), 10) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }
  
  const expectedVerifier = 11 - (sum % 11);
  const validVerifier =
    expectedVerifier === 11
      ? '0'
      : expectedVerifier === 10
      ? 'k'
      : expectedVerifier.toString();
      
  return verifier === validVerifier;
};

// Validador de RUT chileno
const chileanRutValidator = (value: string): boolean => {
  if (!value) return true;
  
  // Limpiar el RUT de puntos y guiones
  const cleanRut = value.replace(/\./g, '').replace(/-/g, '');
  
  // Verificar formato básico
  const rutPattern = /^[0-9]{7,8}[0-9kK]$/;
  if (!rutPattern.test(cleanRut)) {
    return false;
  }
  
  const number = cleanRut.slice(0, -1);
  const verifier = cleanRut.slice(-1).toLowerCase();
  
  let sum = 0;
  let multiplier = 2;
  
  for (let i = number.length - 1; i >= 0; i--) {
    sum += parseInt(number.charAt(i), 10) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }
  
  const expectedVerifier = 11 - (sum % 11);
  const validVerifier =
    expectedVerifier === 11
      ? '0'
      : expectedVerifier === 10
      ? 'k'
      : expectedVerifier.toString();
      
  return verifier === validVerifier;
};

// Función para formatear RUT chileno
export const formatRut = (value: string): string => {
  if (!value) return value;
  
  // Limpiar el valor
  const cleanValue = value.replace(/\./g, '').replace(/-/g, '').replace(/[^0-9kK]/gi, '');
  
  if (cleanValue.length === 0) return '';
  
  // Separar número y dígito verificador
  const number = cleanValue.slice(0, -1);
  const verifier = cleanValue.slice(-1);
  
  // Formatear número con puntos
  let formattedNumber = '';
  for (let i = number.length - 1, j = 0; i >= 0; i--, j++) {
    if (j > 0 && j % 3 === 0) {
      formattedNumber = '.' + formattedNumber;
    }
    formattedNumber = number[i] + formattedNumber;
  }
  
  return `${formattedNumber}-${verifier}`;
};

// Esquema base para campos de texto
export const textFieldSchema = z.string().min(1, 'Este campo es obligatorio');

// Esquema para email
export const emailFieldSchema = z
  .string()
  .min(1, 'Este campo es obligatorio')
  .email('Debe completar este campo obligatorio con un correo válido');

// Esquema para DNI chileno
export const chileanDniSchema = z
  .string()
  .min(1, 'Este campo es obligatorio')
  .refine(chileanDniValidator, {
    message: 'Utiliza el siguiente formato: 11111111-1'
  });

// Esquema para RUT chileno
export const chileanRutSchema = z
  .string()
  .min(1, 'Este campo es obligatorio')
  .refine(chileanRutValidator, {
    message: 'Utiliza el siguiente formato: XX.XXX.XXX-X'
  });

// Esquema para select
export const selectFieldSchema = z
  .string()
  .min(1, 'Debe seleccionar una opción');

// Esquema para checkbox
export const checkboxFieldSchema = z.boolean().default(false);

// Esquema para fecha
export const dateFieldSchema = z
  .string()
  .min(1, 'Este campo es obligatorio')
  .refine((value) => {
    if (!value) return false;
    const date = new Date(value);
    return !isNaN(date.getTime());
  }, {
    message: 'Debe seleccionar una fecha válida'
  });

// Esquema para hora
export const timeFieldSchema = z
  .string()
  .min(1, 'Este campo es obligatorio')
  .refine((value) => {
    if (!value) return false;
    const timePattern = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timePattern.test(value);
  }, {
    message: 'Debe seleccionar una hora válida'
  });

// Función para crear esquema dinámico basado en metadata
export const createFieldSchema = (metadata: {
  type: string;
  kind?: string;
  mandatory: boolean;
  email?: boolean;
  // Allow reading definition for selects (actual Metadata includes it)
  definition?: {
    multiselect?: boolean;
    maxOptions?: number | null;
  };
}) => {
  let schema: z.ZodTypeAny;

  if (metadata.type === 'email' || metadata.email) {
    schema = emailFieldSchema;
  } else if (metadata.type === 'text' && metadata.kind === 'CHILEAN_DNI') {
    schema = chileanDniSchema;
  } else if (metadata.type === 'rut') {
    schema = chileanRutSchema;
  } else if (metadata.type === 'select') {
    const isMulti = !!metadata.definition?.multiselect;
    if (isMulti) {
      let arraySchema = z.array(z.string());
      if (metadata.mandatory) {
        arraySchema = arraySchema.min(1, 'Debe seleccionar al menos una opción');
      }
      const max = metadata.definition?.maxOptions ?? null;
      if (typeof max === 'number' && max !== null) {
        arraySchema = arraySchema.max(max, `Debe seleccionar como máximo ${max} opción${max === 1 ? '' : 'es'}`);
      }
      schema = arraySchema;
    } else {
      schema = selectFieldSchema;
    }
  } else if (metadata.type === 'checkbox') {
    schema = checkboxFieldSchema;
  } else if (metadata.type === 'date') {
    schema = dateFieldSchema;
  } else if (metadata.type === 'time') {
    schema = timeFieldSchema;
  } else if (metadata.type === 'text') {
    schema = textFieldSchema;
  } else {
    schema = textFieldSchema;
  }

  if (!metadata.mandatory) {
    // Para campos no obligatorios:
    // - checkbox: forzar default false
    // - string-like: permitir undefined o cadena vacía
    if (metadata.type === 'checkbox') {
      return z.boolean().default(false);
    }
    if (metadata.type === 'select' && metadata.definition?.multiselect) {
      // For multiselect, allow empty array or undefined
      return schema.optional().or(z.array(z.string()).length(0));
    }
    return schema.optional().or(z.literal(''));
  }

  return schema;
}; 